import React, { useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import companyConfig from '../config/companyConfig';
import { API_URL, API_ENDPOINTS } from '../config/apiConfig';

function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Find the service by slug
  const service = companyConfig.services.find(s => s.slug === slug);

  // Booking Modal State
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingStatus, setBookingStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaRef = useRef(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [bookingFormData, setBookingFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    preferredDate: '',
    message: ''
  });

  // If service not found, redirect to services page
  if (!service) {
    return (
      <div className="service-not-found">
        <div className="not-found-content">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>Service Not Found</h2>
          <p>The service you're looking for doesn't exist.</p>
          <Link to="/services" className="btn-primary">
            <i className="fas fa-arrow-left"></i> Back to Services
          </Link>
        </div>
      </div>
    );
  }

  const openBookingModal = () => setIsBookingModalOpen(true);
  const closeBookingModal = () => {
    setIsBookingModalOpen(false);
    setBookingStatus({ type: '', message: '' });
    setRecaptchaValue(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setBookingFormData({
      fullName: '',
      email: '',
      phone: '',
      company: '',
      preferredDate: '',
      message: ''
    });
  };

  const handleBookingInputChange = (e) => {
    const { name, value } = e.target;
    setBookingFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();

    // Validate reCAPTCHA (only if enabled)
    if (companyConfig.recaptcha.enabled !== false && !recaptchaValue) {
      setBookingStatus({ type: 'error', message: 'Please complete the reCAPTCHA verification before submitting.' });
      return;
    }

    setIsSubmitting(true);
    setBookingStatus({ type: '', message: '' });

    try {
      const bookingData = {
        name: bookingFormData.fullName,
        email: bookingFormData.email,
        phone: bookingFormData.phone,
        company: bookingFormData.company,
        serviceType: service.title,
        message: bookingFormData.message || 'Service booking inquiry'
      };

      // Determine URL: If plain endpoint, prepend API_URL. If full URL (Formspree), use as is.
      const submitUrl = API_ENDPOINTS.booking.startsWith('http')
        ? API_ENDPOINTS.booking
        : `${API_URL}${API_ENDPOINTS.booking}`;

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json' // Required for Formspree
        },
        body: JSON.stringify(bookingData)
      });

      const data = await response.json();

      if (response.ok) {
        setBookingStatus({
          type: 'success',
          message: 'Thank you for your interest! We will contact you within 24 hours.'
        });
        setTimeout(() => {
          closeBookingModal();
        }, 3000);
      } else {
        setBookingStatus({
          type: 'error',
          message: data.error || 'Failed to submit booking. Please try again.'
        });
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      setBookingStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="service-detail-page">
      {/* Hero Section */}
      <section
        className="service-detail-hero"
        style={{ backgroundImage: `url(${service.image})` }}
      >
        <div className="service-hero-overlay"></div>
        <div className="service-hero-content">
          <div className="service-hero-badge">
            <i className={service.icon}></i>
          </div>
          <h1 className="service-hero-title">{service.title}</h1>
          <p className="service-hero-subtitle">{service.shortDescription}</p>
          <div className="service-hero-actions">
            <button onClick={openBookingModal} className="btn-hero-primary">
              <i className="fas fa-calendar-check"></i> Book Consultation
            </button>
            <Link to="/contact" className="btn-hero-secondary">
              <i className="fas fa-envelope"></i> Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="service-detail-content">
        <div className="service-content-container">
          {/* Overview */}
          <div className="service-overview">
            <h2 className="section-title">
              <i className="fas fa-info-circle"></i> Overview
            </h2>
            <p className="service-full-description">{service.fullDescription}</p>
          </div>

          {/* Features */}
          <div className="service-features">
            <h2 className="section-title">
              <i className="fas fa-check-double"></i> Key Features
            </h2>
            <div className="features-grid">
              {service.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <i className="fas fa-check-circle"></i>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits (if available) */}
          {service.benefits && service.benefits.length > 0 && (
            <div className="service-benefits">
              <h2 className="section-title">
                <i className="fas fa-trophy"></i> Business Benefits
              </h2>
              <div className="benefits-grid">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="benefit-card">
                    <div className="benefit-icon">
                      <i className="fas fa-star"></i>
                    </div>
                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA Section */}
          <div className="service-cta">
            <div className="cta-content">
              <h3>Ready to Get Started?</h3>
              <p>Let's discuss how {service.title} can transform your business</p>
              <button onClick={openBookingModal} className="btn-cta-primary">
                <i className="fas fa-rocket"></i> Schedule a Consultation
              </button>
            </div>
          </div>

          {/* Related Services */}
          <div className="related-services">
            <h2 className="section-title">
              <i className="fas fa-layer-group"></i> Related Services
            </h2>
            <div className="related-services-grid">
              {companyConfig.services
                .filter(s => s.id !== service.id)
                .slice(0, 3)
                .map(relatedService => (
                  <Link
                    key={relatedService.id}
                    to={`/services/${relatedService.slug}`}
                    className="related-service-card"
                  >
                    <div className="related-service-icon">
                      <i className={relatedService.icon}></i>
                    </div>
                    <h4>{relatedService.title}</h4>
                    <p>{relatedService.shortDescription}</p>
                    <span className="learn-more">
                      Learn More <i className="fas fa-arrow-right"></i>
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {isBookingModalOpen && (
        <div className="booking-modal-overlay" onClick={closeBookingModal}>
          <div className="booking-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeBookingModal}>
              <i className="fas fa-times"></i>
            </button>

            <div className="modal-header">
              <i className={service.icon}></i>
              <h3>Book {service.title} Consultation</h3>
              <p>Fill out the form below and we'll get back to you within 24 hours</p>
            </div>

            <form onSubmit={handleBookingSubmit} className="booking-form">
              {bookingStatus.message && (
                <div className={`booking-status-message ${bookingStatus.type}`}>
                  <i className={`fas ${bookingStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                  <span>{bookingStatus.message}</span>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fullName">
                    <i className="fas fa-user"></i> Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={bookingFormData.fullName}
                    onChange={handleBookingInputChange}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope"></i> Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={bookingFormData.email}
                    onChange={handleBookingInputChange}
                    placeholder="john@company.com"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">
                    <i className="fas fa-phone"></i> Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={bookingFormData.phone}
                    onChange={handleBookingInputChange}
                    placeholder="+1 (555) 123-4567"
                    required
                  />
                  <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block' }}>
                    <i className="fas fa-info-circle"></i> Include country code (e.g., +1, +91)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="company">
                    <i className="fas fa-building"></i> Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={bookingFormData.company}
                    onChange={handleBookingInputChange}
                    placeholder="Your Company"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="preferredDate">
                  <i className="fas fa-calendar"></i> Preferred Date
                </label>
                <input
                  type="date"
                  id="preferredDate"
                  name="preferredDate"
                  value={bookingFormData.preferredDate}
                  onChange={handleBookingInputChange}
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="message">
                  <i className="fas fa-comment"></i> Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={bookingFormData.message}
                  onChange={handleBookingInputChange}
                  placeholder="Tell us about your requirements..."
                  rows="4"
                ></textarea>
                <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '5px', display: 'block' }}>
                  <i className="fas fa-info-circle"></i> Please provide at least 15 words for better clarity
                </small>
              </div>

              {/* reCAPTCHA */}
              {companyConfig.recaptcha.enabled !== false && (
                <div className="form-group full-width" style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={companyConfig.recaptcha.siteKey}
                    onChange={(value) => setRecaptchaValue(value)}
                  />
                </div>
              )}

              <button type="submit" className="btn-submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Submitting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane"></i> Submit Request
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceDetail;
